import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { EmptyState, SearchLg, UploadCloud01, Folder } from '@opendate-app/setlist';

import RecursiveFolders from "./RecursiveFolders";
import FolderedItemRow from "./FolderedItemRow";
import DragOverlayContent from "./DragOverlayContent";
import Breadcrumbs from "./Breadcrumbs";
import GridView from "./GridView";
import ContextMenu from "./ContextMenu";
import SelectionBar from "./SelectionBar";
import SelectTargetFolder from "./SelectTargetFolder";
import FolderDownloadModal from "./FolderDownloadModal";
import ShareFolderModal from "./ShareFolderModal";
import ShareFileModal from "./ShareFileModal";
import SetPermissionsPopup from "../../SetPermissionsPopup/components/SetPermissionsPopup";

import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { readDirectoryRecursively } from '../utils/folderUploadHelper';
import Popup from "reactjs-popup";
import ContentEditable from 'react-contenteditable'

import {
  MOVE_TO_FOLDER_CONTEXT,
  NEW_FOLDER_CONTEXT,
  UPLOAD_FILES_CONTEXT,
  COPY_FILE_CONTEXT
} from '../constants/folderViewConstants';

const popupStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "840px",
  "maxWidth": "calc(100vw - 40px)",
  "padding": "20px"
}

const deletePopupContentStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "490px",
  "padding": "14px 16px"
}

const movePopupContentStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "90%",
  "maxWidth": "560px",
  "padding": "25px"
}

const copyFilePopupContentStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "90%",
  "maxWidth": "560px",
  "padding": "25px"
}

const newFolderPopupContentStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "90%",
  "maxWidth": "560px",
  "padding": "25px"
}

const deleteModalContentStyle = {
  "background": "var(--od-surface, #fff)",
  "textAlign": "left",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "500px",
  "padding": "15px"
}

const calculateNumberOfFilesToDestroy = (folderedItemsToDestroy) => {
  return folderedItemsToDestroy
    .filter((fi) => !fi.item.is_folder)
    .length;
}

const calculateNumberOfFoldersToDestroy = (folderedItemsToDestroy) => {
  return folderedItemsToDestroy
    .filter((fi) => fi.item.is_folder)
    .length;
}

const canMoveStagedFolderedItems = (folderedItemsToMove, targetFolder) => {
  return (
    folderedItemsToMove.length > 0
      && targetFolder
      && targetFolder.id
  );
};

const canCreateNewFolder = (targetFolder, newFolderName) => {
  return (
    targetFolder
      && targetFolder.id
      && newFolderName.length > 0
  );
};

const canUploadFiles = (targetFolder, selectedFilesToUpload, isLoadingUploadedFiles) => {
  return (
    targetFolder
      && targetFolder.id
      && selectedFilesToUpload.length > 0
      && !isLoadingUploadedFiles
  );
};

const canCopyCalendarEventAttachment = (targetFolder, calendarEventAttachmentToCopy, isCopyingCalendarEventAttachment) => {
  return (
    targetFolder
      && targetFolder.id
      && calendarEventAttachmentToCopy
      && calendarEventAttachmentToCopy.human_file_name
      && calendarEventAttachmentToCopy.human_file_name.length > 0
      && !isCopyingCalendarEventAttachment
  );
};

const depthWidth = (depth) => {
  var firstGutter = 49;

  switch(depth) {
    case 0:
      return 0;
      break;
    case 1:
      return firstGutter;
      break;
    default:
      var depthAfterFirst = (depth - 1);
      var incrementSpace  = 28;

      return firstGutter + (incrementSpace * depthAfterFirst);
  }
}

const FolderView = ({
  csrfToken,
  team,
  confirm,
  openCalendarEventAttachment,
  closeCalendarEventAttachmentModal,
  deleteCalendarEventAttachment,
  fetchCalendarEventAttachment,
  openCalendarEventAttachmentPdfNumberOfPages,
  openCalendarEventAttachmentPdfNumberOfPagesChanged,
  editOpenCalendarEventAttachmentName,
  updateCalendarEventAttachment,
  setOpenCalendarEventAttachmentNameEl,
  endEditOpenCalendarEventAttachmentName,
  currentUser,
  folder,
  createFolder,
  toggleFolder,
  selectedFolderedItems,
  togglefolderedItemSelected,
  folderedItemsToDestroy,
  stageFolderedItemsForDestroy,
  deleteStagedFolderedItems,
  copiedFolderedItem,
  copiedFolderedItemChanged,
  copiedFolderedItemNameChanged,
  updateFolderedItemName,
  folderedItemsToMove,
  stageFolderedItemsForMove,
  moveStagedFolderedItems,
  fetchPermissionSet,
  folderedItemUpdated,
  fetchFolderedItemsForFolder,
  targetFolder,
  targetFolderChanged,
  folderCopy,
  folderCopyChanged,
  fetchFolderedItemsForFolderCopy,
  toggleFolderCopy,
  onFolderChanged,
  permissionSet,
  permissionSetChanged,
  permissionRuleEnabledClicked,
  selectAllPermissionRulesClicked,
  submitPermissionSet,
  onPermissionSetSaved,
  guestTeamMemberships,
  promotersWithPermission,
  headerComponent,
  menuComponent,
  isAddingFolder,
  isAddingFolderChanged,
  newFolderName,
  newFolderNameChanged,
  isUploadingFiles,
  isUploadingFilesChanged,
  selectedFilesToUpload,
  selectedFilesToUploadChanged,
  uploadFilesToFolder,
  isLoadingUploadedFiles,
  canSelectRootFolderAsTarget,
  fetchGuestsAndPromotersForPermissions,
  searchTerm,
  targetFolderSearchTerm,
  targetFolderSearchTermChanged,
  calendarEventAttachmentToCopy,
  calendarEventAttachmentToCopyChanged,
  createCalendarEventAttachmentCopy,
  isCopyingCalendarEventAttachment,
  teamRootFolder,
  draggedFilesToUpload,
  draggedFilesToUploadChanged,
  showConfirmIdColumn,
  folderChanged,
  uploadFolderStructure,
  navigateToFolder,
  onDropWithoutSelection,
  noSelectionDropFolder
}) => {
  var numberOfFilesToDestroy = calculateNumberOfFilesToDestroy(folderedItemsToDestroy);
  var numberOfFoldersToDestroy = calculateNumberOfFoldersToDestroy(folderedItemsToDestroy);

  const [sortBy, setSortBy] = useState(
    showConfirmIdColumn ? { column: 'calendar_event_id', direction: 'desc' } : {}
  );
  const [viewMode, setViewMode] = useState('list');
  const [dragData, setDragData] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [viewFolderId, setViewFolderId] = useState(null);
  const [shareFolderItem, setShareFolderItem] = useState(null);
  const [shareFileItem, setShareFileItem] = useState(null);
  const [folderDownloadOptions, setFolderDownloadOptions] = useState(null);
  const [folderDownloadEmail, setFolderDownloadEmail] = useState("");
  const [folderDownloadSubmitting, setFolderDownloadSubmitting] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (folderedItemsToMove.length > 0) {
      var updated = Object.assign({}, folder, {
        lastRequestData: undefined,
        foldered_items: []
      });
      folderCopyChanged(updated);
    }
  }, [folderedItemsToMove]);

  useEffect(() => {
    if (Object.keys(calendarEventAttachmentToCopy).length > 0) {
      var updated = Object.assign({}, folder, {
        lastRequestData: undefined,
        foldered_items: []
      });
      folderCopyChanged(updated);
    }
  }, [calendarEventAttachmentToCopy]);

  useEffect(() => {
    if (isAddingFolder) {
      var updated = Object.assign({}, folder, {
        lastRequestData: undefined,
        foldered_items: []
      });
      folderCopyChanged(updated);
      newFolderNameChanged("New Folder");
    }
  }, [isAddingFolder]);

  useEffect(() => {
    if (isUploadingFiles) {
      var updated = Object.assign({}, folder, {
        lastRequestData: undefined,
        foldered_items: []
      });
      folderCopyChanged(updated);
      selectedFilesToUploadChanged({});
    }
  }, [isUploadingFiles]);

  const findFolderInTree = (root, targetId) => {
    if (root.id === targetId) return root;
    if (!root.foldered_items) return null;
    for (var i = 0; i < root.foldered_items.length; i++) {
      var fi = root.foldered_items[i];
      if (fi.item.is_folder) {
        var found = findFolderInTree(fi.item, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const assetCenterHref = "/teams/" + team.id + "/asset_center/events";

  const buildBreadcrumbPath = (root, targetId) => {
    var path = [];
    var search = function(f) {
      var isRoot = f.id === root.id;
      path.push({
        id: f.id,
        name: isRoot ? "Asset Center" : f.human_file_name
      });
      if (f.id === targetId) return true;
      if (f.foldered_items) {
        for (var i = 0; i < f.foldered_items.length; i++) {
          if (f.foldered_items[i].item.is_folder) {
            if (search(f.foldered_items[i].item)) return true;
          }
        }
      }
      path.pop();
      return false;
    };
    search(root);
    return path;
  };

  const currentViewFolder = viewFolderId
    ? (findFolderInTree(folder, viewFolderId) || folder)
    : folder;

  const handleNavigateToFolder = (folderId) => {
    if (folderId === folder.id) {
      setViewFolderId(null);
      return;
    }
    var targetFolder = findFolderInTree(folder, folderId);
    if (targetFolder) {
      setViewFolderId(folderId);
      if (!targetFolder.extended) {
        toggleFolder(targetFolder);
      }
      if (!targetFolder.lastRequestData) {
        fetchFolderedItemsForFolder(team, targetFolder, searchTerm, sortBy);
      }
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = ({ active }) => {
    const activeFoldered = active.data.current.foldered;
    const isSelected = selectedFolderedItems.some((fi) => fi.id === activeFoldered.id);

    if (isSelected && selectedFolderedItems.length > 1) {
      setDragData({ items: selectedFolderedItems });
    } else {
      setDragData({ items: [activeFoldered] });
    }
  };

  const handleDragEnd = ({ active, over }) => {
    setDragData(null);

    if (!active || !over) return;

    const overData = over.data.current;
    if (!overData || !overData.foldered) return;

    const dropTarget = overData.foldered;
    if (!dropTarget.item.is_folder) return;
    if (!dropTarget.item.can_be_move_target) return;

    const activeFoldered = active.data.current.foldered;
    const isSelected = selectedFolderedItems.some((fi) => fi.id === activeFoldered.id);
    const itemsToMove = (isSelected && selectedFolderedItems.length > 1)
      ? selectedFolderedItems
      : [activeFoldered];

    const isSelfDrop = itemsToMove.some((fi) => fi.id === dropTarget.id);
    if (isSelfDrop) return;

    const isDropOnChild = itemsToMove.some(
      (fi) => fi.item.is_folder && fi.item.id === dropTarget.item.id
    );
    if (isDropOnChild) return;

    moveStagedFolderedItems(csrfToken, team, itemsToMove, dropTarget.item);
  };

  const getDropUploadTarget = () => {
    if (!onDropWithoutSelection) return currentViewFolder;

    if (selectedFolderedItems.length > 0) {
      var firstSelected = selectedFolderedItems[0];
      if (firstSelected.item.is_folder) {
        return findFolderInTree(folder, firstSelected.item.id) || currentViewFolder;
      }
      return findFolderInTree(folder, firstSelected.folder_id) || currentViewFolder;
    }

    return noSelectionDropFolder || currentViewFolder;
  };

  const getDropOverlayText = () => {
    if (!onDropWithoutSelection) return "Drop files to upload to " + currentViewFolder.human_file_name;

    if (selectedFolderedItems.length > 0) {
      var target = getDropUploadTarget();
      return "Drop files to upload to " + target.human_file_name;
    }

    return "Drop files to upload to " + (noSelectionDropFolder || currentViewFolder).human_file_name;
  };

  const getFolderUnderCursor = (event) => {
    if (!event || !event.clientX) return null;
    const elements = document.elementsFromPoint(event.clientX, event.clientY);
    for (const el of elements) {
      const row = el.closest('[data-folder-id]');
      if (row) {
        const folderId = parseInt(row.getAttribute('data-folder-id'));
        if (folderId) return findFolderInTree(folder, folderId);
      }
    }
    return null;
  };

  const handleDesktopFileDrop = async (acceptedFiles, _rejectedFiles, event) => {
    const items = event && event.dataTransfer ? event.dataTransfer.items : [];
    const directories = [];
    const files = [];
    const folderUnderCursor = getFolderUnderCursor(event);
    const uploadTarget = folderUnderCursor || getDropUploadTarget();

    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null;
      if (entry && entry.isDirectory) {
        const folderTree = await readDirectoryRecursively(entry);
        directories.push(folderTree);
      } else if (entry && entry.isFile) {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }

    var shouldRedirect = onDropWithoutSelection && selectedFolderedItems.length === 0;

    if (shouldRedirect) {
      var uploadPromises = [];

      if (directories.length > 0) {
        for (const folderTree of directories) {
          uploadPromises.push(uploadFolderStructure(csrfToken, team, confirm, folderTree, uploadTarget));
        }
      }

      if (files.length > 0) {
        uploadPromises.push(uploadFilesToFolder(csrfToken, team, confirm, files, uploadTarget, true, false));
      }

      try {
        await Promise.all(uploadPromises);
      } catch (e) {
        toast.error("Some files failed to upload", { position: toast.POSITION.TOP_CENTER });
      }
      setTimeout(() => onDropWithoutSelection(), 0);
    } else {
      if (directories.length > 0) {
        for (const folderTree of directories) {
          uploadFolderStructure(csrfToken, team, confirm, folderTree, uploadTarget);
        }
      }

      if (files.length > 0) {
        uploadFilesToFolder(csrfToken, team, confirm, files, uploadTarget, true, false);
      }
    }
  };

  const handleContextMenu = (e, foldered) => {
    e.preventDefault();
    setContextMenu({ position: { x: e.clientX, y: e.clientY }, foldered: foldered });
  };

  const handleContextMenuDownload = (foldered) => {
    setContextMenu(null);
    if (foldered.item.is_folder) {
      handleFolderDownload(foldered.item);
    } else {
      window.location.href = "/teams/" + team.id + "/calendar_event_attachments/" + foldered.item.id + "/download";
    }
  };

  // Trigger a native browser download for a folder zip.
  //
  // Native anchor-click (not fetch+blob) is critical for the "Download Now"
  // modal path: large folders take 30+ seconds for the server to zip up,
  // and a programmatic <a>.click() inside a .then() resolves outside the
  // original user-gesture window — most browsers then silently refuse the
  // download. An anchor-click placed directly inside the user gesture
  // delegates to the browser's native download path, which is unaffected
  // by gesture-expiry.
  const downloadFolderNow = (folder, deliveryMethod) => {
    let url = "/teams/" + team.id + "/folders/" + folder.id + "/download";
    if (deliveryMethod) {
      url += "?delivery_method=" + deliveryMethod;
    }

    const a = document.createElement("a");
    a.href = url;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();

    toast.info("Preparing your download. This may take a moment for large folders.");
  };

  const handleFolderDownload = (folder) => {
    const url = "/teams/" + team.id + "/folders/" + folder.id + "/download_options";

    fetch(url, {
      method: "GET",
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    }).then((response) => {
      if (!response.ok) {
        toast.error("Something went wrong. Please try again.");
        return;
      }

      return response.json().then((data) => {
        if (data.requires_async_download) {
          setFolderDownloadOptions(Object.assign({}, data, { folder: folder }));
          setFolderDownloadEmail(data.email || currentUser.email || "");
        } else {
          downloadFolderNow(folder);
        }
      });
    }).catch(() => {
      toast.error("Something went wrong. Please try again.");
    });
  };

  const closeFolderDownloadModal = () => {
    setFolderDownloadOptions(null);
    setFolderDownloadEmail("");
    setFolderDownloadSubmitting(false);
  };

  const handleFolderDownloadNow = () => {
    if (!folderDownloadOptions) return;

    const folder = folderDownloadOptions.folder;
    closeFolderDownloadModal();
    downloadFolderNow(folder, "download");
  };

  const handleFolderEmailLink = () => {
    if (!folderDownloadOptions) return;

    setFolderDownloadSubmitting(true);

    const folder = folderDownloadOptions.folder;
    const url = "/teams/" + team.id + "/folders/" + folder.id + "/email_download";

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify({ email: folderDownloadEmail }),
    }).then((response) => {
      if (response.status === 202) {
        return response.json().then((data) => {
          toast.info(data.message || "Your download is being prepared. You will receive an email when it's ready.");
          closeFolderDownloadModal();
        });
      }

      return response.json().then((data) => {
        toast.error((data.errors || ["Something went wrong. Please try again."]).join(", "));
        setFolderDownloadSubmitting(false);
      });
    }).catch(() => {
      toast.error("Something went wrong. Please try again.");
      setFolderDownloadSubmitting(false);
    });
  };

  const handleContextMenuCopy = (foldered) => {
    calendarEventAttachmentToCopyChanged(foldered.item);
  };

  const handleContextMenuMove = (foldered, isMultiSelect) => {
    if (isMultiSelect) {
      stageFolderedItemsForMove(selectedFolderedItems);
    } else {
      stageFolderedItemsForMove([foldered]);
    }
  };

  const handleContextMenuRename = (foldered) => {
    copiedFolderedItemChanged(foldered);
  };

  const handleContextMenuSetPermissions = (foldered) => {
    var permissionSetToFetch;
    if (foldered.permission_set_id) {
      permissionSetToFetch = { id: foldered.permission_set_id };
    } else {
      permissionSetToFetch = { element_id: foldered.id, element_type: "FolderedItem" };
    }
    permissionSetToFetch = Object.assign({}, permissionSetToFetch, {
      _element_type_human_name: "Asset"
    });
    if (foldered.calendar_event_id) {
      fetchGuestsAndPromotersForPermissions(team, foldered.calendar_event_id);
    }
    fetchPermissionSet(team, permissionSetToFetch, (permissionSet) => {
      var updated = Object.assign({}, foldered, { permission_set_id: permissionSet.id });
      folderedItemUpdated(updated);
    });
  };

  const handleContextMenuShare = (foldered) => {
    if (foldered.item.is_folder) {
      setShareFolderItem(foldered);
    } else {
      setShareFileItem(foldered);
    }
  };

  const handleContextMenuDelete = (foldered, isMultiSelect) => {
    if (isMultiSelect) {
      stageFolderedItemsForDestroy(selectedFolderedItems);
    } else {
      stageFolderedItemsForDestroy([foldered]);
    }
  };

  const handleClearSelection = () => {
    selectedFolderedItems.forEach((fi) => togglefolderedItemSelected(fi));
  };

  const shareableItem = selectedFolderedItems.length === 1
    && currentUser.can_manage_calendar_event_attachments
    && selectedFolderedItems[0].item.user_can_change
    ? selectedFolderedItems[0]
    : null;

  return (
    <div>
      <Dropzone noClick noDragEventsBubbling onDrop={handleDesktopFileDrop}>
        {({ getRootProps, getInputProps, isDragActive }) => (
      <div {...getRootProps()} className="card-body asset-center-body">
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className="asset-center-drop-overlay">
            <div className="drop-overlay-text">
              <i className="fas fa-cloud-upload"></i>
              {getDropOverlayText()}
            </div>
          </div>
        ) : null}
        <div className={"row"}>
          <div className={"col d-flex align-items-center " + (folder.calendar_event_attachments && folder.calendar_event_attachments.length > 0 ? "pb-4" : "pb-3")}>
            {headerComponent()}
          </div>
          {currentUser.can_manage_calendar_event_attachments ? (
            <div className="col-xs-auto pb-3 pr-3 d-flex align-items-center">
              <a href="#"
                 onClick={
                   (e) => {
                     e.preventDefault();
                     isAddingFolderChanged(true);
                   }
                 }
                 className="btn btn-external btn-sm ml-2">
                <i className="fas fa-folder-plus"></i>
                &nbsp;
                <strong>New Folder</strong>
              </a>
              <a href="#"
                 onClick={
                   (e) => {
                     e.preventDefault();
                     isUploadingFilesChanged(true);
                   }
                 }
                 className="btn btn-external btn-sm ml-2">
                <i className="fas fa-cloud-upload"></i>
                &nbsp;
                <strong>Upload Files</strong>
              </a>
              {menuComponent ? menuComponent() : null}
              <div className="btn-group ml-2 view-mode-toggle" role="group">
                <a href="#"
                   className={"btn btn-sm " + (viewMode === 'list' ? "btn-primary" : "btn-external")}
                   onClick={(e) => { e.preventDefault(); setViewMode('list'); setViewFolderId(null); }}>
                  <i className="fas fa-list"></i>
                </a>
                <a href="#"
                   className={"btn btn-sm " + (viewMode === 'grid' ? "btn-primary" : "btn-external")}
                   onClick={(e) => { e.preventDefault(); setViewMode('grid'); }}>
                  <i className="fas fa-th"></i>
                </a>
              </div>
            </div>
          ) : null}
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
        <div className={'row responsive-folder-view folder-content-area' + (showConfirmIdColumn ? ' responsive-folder-view--with-event-id' : '')}>
          <div className='col-12'>
            {!confirm.id ? (
              <div className="d-none d-md-block">
                {viewFolderId ? (
                  <Breadcrumbs
                    breadcrumbs={buildBreadcrumbPath(folder, viewFolderId)}
                    onNavigate={handleNavigateToFolder}
                  />
                ) : folder.breadcrumb_path && folder.breadcrumb_path.length > 1 ? (
                  <Breadcrumbs
                    breadcrumbs={folder.breadcrumb_path.map(function(crumb, index) {
                      if (index === 0) {
                        return { id: crumb.id, name: "Asset Center", href: assetCenterHref };
                      }
                      return crumb;
                    })}
                    onNavigate={handleNavigateToFolder}
                  />
                ) : null}
              </div>
            ) : null}
            {viewMode === 'grid' ? (
              <GridView
                folder={currentViewFolder}
                currentUser={currentUser}
                team={team}
                selectedFolderedItems={selectedFolderedItems}
                togglefolderedItemSelected={togglefolderedItemSelected}
                fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                fetchFolderedItemsForFolder={fetchFolderedItemsForFolder}
                searchTerm={searchTerm}
                sortBy={sortBy}
                onNavigateToFolder={handleNavigateToFolder}
                onContextMenu={(e, foldered) => {
                  e.preventDefault();
                  setContextMenu({ position: { x: e.clientX, y: e.clientY }, foldered: foldered });
                }}
              />
            ) : (
            <React.Fragment>
            <div className="row m-0 folder-view-header">
              <div className="col-12 p-0 d-flex align-items-center small folder-view-header-border">
                {showConfirmIdColumn ? (
                  <>
                    <div className="folder-view-name-cell folder-view-header-cell--name">
                      <a onClick={
                            (e) => {
                              e.preventDefault();

                              if(Object.keys(sortBy).length === 0 || sortBy.column !== "item_name"){
                                setSortBy({
                                  column: "item_name",
                                  direction: "asc"
                                });
                              } else if(sortBy.column === "item_name" && sortBy.direction === "asc"){
                                setSortBy({
                                  column: "item_name",
                                  direction: "desc"
                                });
                              } else if(sortBy.column === "item_name" && sortBy.direction === "desc"){
                                setSortBy({});
                              }

                              var updated = Object.assign({}, folder, {
                                isLoadingPaginatedData: undefined,
                                lastRequestData: undefined,
                                foldered_items: []
                              });

                              folderChanged(updated);
                            }
                          }
                          className="text-dark"
                          href="#">
                        <strong className='mr-1'>
                          Name
                        </strong>
                        {sortBy.column === "item_name" && sortBy.direction === "asc" ? (
                          <i className="fas fa-sort-up"></i>
                        ) : sortBy.column === "item_name" && sortBy.direction === "desc" ? (
                          <i className="fas fa-sort-down"></i>
                        ) : (
                          <i className="fas fa-sort"></i>
                        )}
                      </a>
                    </div>
                    <div className="folder-view-event-date-cell folder-view-header-cell">
                      <strong>Event Date</strong>
                    </div>
                    <div className="folder-view-event-cell folder-view-header-cell">
                      <a onClick={
                            (e) => {
                              e.preventDefault();

                              if(Object.keys(sortBy).length === 0 || sortBy.column !== "calendar_event_id"){
                                setSortBy({
                                  column: "calendar_event_id",
                                  direction: "asc"
                                });
                              } else if(sortBy.column === "calendar_event_id" && sortBy.direction === "asc"){
                                setSortBy({
                                  column: "calendar_event_id",
                                  direction: "desc"
                                });
                              } else if(sortBy.column === "calendar_event_id" && sortBy.direction === "desc"){
                                setSortBy({});
                              }

                              var updated = Object.assign({}, folder, {
                                isLoadingPaginatedData: undefined,
                                lastRequestData: undefined,
                                foldered_items: []
                              });

                              folderChanged(updated);
                            }
                          }
                          className="text-dark"
                          href="#">
                        <strong className='mr-1'>
                          Event ID
                        </strong>
                        {sortBy.column === "calendar_event_id" && sortBy.direction === "asc" ? (
                          <i className="fas fa-sort-up"></i>
                        ) : sortBy.column === "calendar_event_id" && sortBy.direction === "desc" ? (
                          <i className="fas fa-sort-down"></i>
                        ) : (
                          <i className="fas fa-sort"></i>
                        )}
                      </a>
                    </div>
                    <div className="folder-view-date-cell folder-view-header-cell">
                      <strong>Date Modified</strong>
                    </div>
                    <div className="folder-view-size-cell folder-view-header-cell">
                      <strong>Size</strong>
                    </div>
                    <div className="folder-view-type-cell folder-view-header-cell">
                      <strong>Type</strong>
                    </div>
                    <div className="folder-view-actions-cell folder-view-header-cell--actions">
                      &nbsp;
                    </div>
                  </>
                ) : (
                  <>
                    <div className="folder-view-name-cell folder-view-header-cell--name">
                      <strong>Name</strong>
                    </div>
                    <div className="folder-view-date-cell folder-view-header-cell">
                      <strong>Date Modified</strong>
                    </div>
                    <div className="folder-view-size-cell folder-view-header-cell">
                      <strong>Size</strong>
                    </div>
                    <div className="folder-view-type-cell folder-view-header-cell">
                      <strong>Type</strong>
                    </div>
                    <div className="folder-view-actions-cell folder-view-header-cell--actions">
                      &nbsp;
                    </div>
                  </>
                )}
              </div>
            </div>
            <RecursiveFolders csrfToken={csrfToken}
                          team={team}
                          fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                          currentUser={currentUser}
                          depth={0}
                          toggleFolder={toggleFolder}
                          selectedFolderedItems={selectedFolderedItems}
                          togglefolderedItemSelected={togglefolderedItemSelected}
                          stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                          copiedFolderedItem={copiedFolderedItem}
                          copiedFolderedItemChanged={copiedFolderedItemChanged}
                          copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                          updateFolderedItemName={updateFolderedItemName}
                          stageFolderedItemsForMove={stageFolderedItemsForMove}
                          rootFolder={folder}
                          fetchPermissionSet={fetchPermissionSet}
                          folderedItemUpdated={folderedItemUpdated}
                          fetchFolderedItemsForFolder={fetchFolderedItemsForFolder}
                          parentFolderedItems={[]}
                          searchTerm={searchTerm}
                          sortBy={sortBy}
                          folder={folder}>
              {(foldered, depth, rootFolder, parentFolderedItems) => (
                <FolderedItemRow csrfToken={csrfToken}
                    team={team}
                    fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                    currentUser={currentUser}
                    folder={folder}
                    depth={depth}
                    depthWidth={depthWidth}
                    toggleFolder={toggleFolder}
                    selectedFolderedItems={selectedFolderedItems}
                    togglefolderedItemSelected={togglefolderedItemSelected}
                    stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                    copiedFolderedItem={copiedFolderedItem}
                    copiedFolderedItemChanged={copiedFolderedItemChanged}
                    copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                    updateFolderedItemName={updateFolderedItemName}
                    stageFolderedItemsForMove={stageFolderedItemsForMove}
                    rootFolder={rootFolder}
                    fetchGuestsAndPromotersForPermissions={fetchGuestsAndPromotersForPermissions}
                    fetchPermissionSet={fetchPermissionSet}
                    folderedItemUpdated={folderedItemUpdated}
                    parentFolderedItems={parentFolderedItems}
                    calendarEventAttachmentToCopyChanged={calendarEventAttachmentToCopyChanged}
                    uploadFilesToFolder={uploadFilesToFolder}
                    showConfirmIdColumn={showConfirmIdColumn}
                    foldered={foldered}
                    onContextMenu={handleContextMenu}
                    onShare={handleContextMenuShare}
                    onFolderDownload={handleFolderDownload} />
              )}
            </RecursiveFolders>
            </React.Fragment>
            )}
            {folder.lastRequestData && folder.foldered_items.length === 0 ? (
              searchTerm.length > 0 && folder.foldered_items_count > 0 ? (
                <div className="row empty-state">
                  <div className="col-12">
                    <EmptyState
                      icon={SearchLg}
                      title="No search results"
                      description="No document or folder names match your search"
                    />
                  </div>
                </div>
              ) : currentUser.can_manage_calendar_event_attachments ? (
                <div className="row">
                  <div className="col-12 empty-state--droppable"
                       onClick={(e) => { e.preventDefault(); isUploadingFilesChanged(true); }}>
                    <EmptyState
                      icon={UploadCloud01}
                      title="Drop files here or click to upload"
                      description="Create folders and add files to manage your team's assets."
                    />
                  </div>
                </div>
              ) : (
                <div className="row empty-state">
                  <div className="col-12">
                    <EmptyState
                      icon={Folder}
                      title="Nothing here yet!"
                      description="Create folders and add files to manage your team's assets."
                    />
                  </div>
                </div>
              )
            ) : null}
          </div>
        </div>
        <DragOverlay>
          <DragOverlayContent dragData={dragData} />
        </DragOverlay>
        </DndContext>
      </div>
        )}
      </Dropzone>
      <FolderDownloadModal
        open={!!folderDownloadOptions}
        folder={folderDownloadOptions ? folderDownloadOptions.folder : {}}
        humanFileSize={folderDownloadOptions ? folderDownloadOptions.human_file_size : ""}
        email={folderDownloadEmail}
        isSubmitting={folderDownloadSubmitting}
        onEmailChange={setFolderDownloadEmail}
        onClose={closeFolderDownloadModal}
        onDownload={handleFolderDownloadNow}
        onEmail={handleFolderEmailLink}
      />
      <Popup
        open={draggedFilesToUpload.length > 0}
        contentStyle={movePopupContentStyle}
        closeOnDocumentClick={false}
      >
        <div>
          <div className="row">
            <div className="col">
              <h5 className="mb-0">
                <strong>
                  <i className="fas fa-file-plus text-primary mr-2"></i>
                  Uploading Files...
                </strong>
              </h5>
            </div>
            <div className="col">
              <div className="float-right css-spinner css-spinner--lg"></div>
            </div>
          </div>
          <div className="mt-4 upload-files-list">
            {draggedFilesToUpload.map((file, index) => (
              <span key={file.name} className="my-2 d-block">{file.name}</span>
            ))}
          </div>
        </div>
      </Popup>
      <Popup
        open={(Object.keys(openCalendarEventAttachment).length > 0)}
        contentStyle={popupStyle}
        closeOnDocumentClick
        className={"popup-modal"}
        onClose={() => closeCalendarEventAttachmentModal()}
      >
        <div className="popup-modal-container" style={{position: "relative"}}>
          <div style={{
            position: "absolute",
            top: "0px",
            right: "0px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            zIndex: 10
          }}>
            {currentUser.can_manage_calendar_event_attachments ? (
              <Popup
                  trigger={
                    <a href="#"
                       className="btn btn-muted hover-with-text-danger">
                      <i className="fas fa-trash"></i>
                    </a>
                  }
                  onOpen={
                    (e) => {
                      e.preventDefault();
                    }
                  }
                  modal
                  contentStyle={deleteModalContentStyle}
                  closeOnDocumentClick
                >
                {close => (
                  <div>
                    <div className="row">
                      <div className="col">
                        <div className="row">
                          <div className="col">
                            <h5 className="modal-title-mb">
                              <strong>Delete asset?</strong>
                            </h5>
                          </div>
                          <div className="col-xs-auto">
                            <a href="#"
                               className="text-dark pr-3"
                               onClick={(e) => {e.preventDefault(); close();}} >
                              <i className="fa fa-times"></i>
                            </a>
                          </div>
                        </div>
                        <p>Are you sure you want to remove this asset?</p>
                        <div className="text-right">
                          <a href="#"
                             onClick={
                               (e) => {
                                 e.preventDefault();
                                 close();
                               }
                             }>
                            Cancel
                          </a>
                          <a href="#"
                             className="btn btn-danger ml-3"
                             onClick={
                               (e) => {
                                 e.preventDefault();
                                 close();
                                 closeCalendarEventAttachmentModal();
                                 deleteCalendarEventAttachment(csrfToken, team, openCalendarEventAttachment);
                               }
                             }>
                            Remove
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Popup>
            ) : null}
            <a href={"/teams/" + team.id + "/calendar_event_attachments/" + openCalendarEventAttachment.id + "/download"}
               className="btn btn-muted">
              <i className="fas fa-download"></i>
            </a>
            <a href="#"
               className="btn btn-muted"
               onClick={(e) => {
                 e.preventDefault();
                 closeCalendarEventAttachmentModal();
               }}>
              <i className="fas fa-times"></i>
            </a>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="row">
                <div className="col" style={{paddingRight: "140px"}}>
                  <h5 className="pt-2">
                    <strong>
                      <i className={"text-primary fas " + openCalendarEventAttachment.fontawesome_icon_class}></i>
                      &nbsp;
                      {openCalendarEventAttachment.editName ? (
                        <ContentEditable
                          html={openCalendarEventAttachment.human_file_name}
                          innerRef={
                            (el) => {
                              if(el && !openCalendarEventAttachment.nameSpanEl){
                                setOpenCalendarEventAttachmentNameEl(el);
                                el.focus();

                                setTimeout(() => {
                                  var selection = window.getSelection();
                                  var range = document.createRange();
                                  range.selectNodeContents(el);
                                  selection.removeAllRanges();
                                  selection.addRange(range);
                                }, 1)
                              }
                            }
                          }
                          className={"single-line"}
                          onKeyDown={
                            (e) => {
                              if (e.keyCode === 13) {
                                e.preventDefault();

                                var newName = openCalendarEventAttachment.nameSpanEl.textContent;
                                updateCalendarEventAttachment(csrfToken, team, openCalendarEventAttachment, newName);
                              }
                            }
                          }
                          onBlur={
                            (e) => {
                              endEditOpenCalendarEventAttachmentName();
                            }
                          }
                          tagName='span'
                        />
                      ) : (
                        <span>{openCalendarEventAttachment.human_file_name}</span>
                      )}
                      {currentUser.can_manage_calendar_event_attachments && !openCalendarEventAttachment.editName ? (
                        <span>
                          &nbsp;
                          &nbsp;
                          <a href="#"
                             onClick={
                               (e) => {
                                 e.preventDefault();
                                 editOpenCalendarEventAttachmentName();
                               }
                             }
                             className="text-muted small">
                            <i className="fas fa-pencil"></i>
                          </a>
                        </span>
                      ) : null}
                    </strong>
                  </h5>
                </div>
              </div>
              <div className="row pt-3">
                <div className="col-12">
                  <strong>Details</strong>
                  <div className="row pt-2">
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="table-responsive">
                      <table className="table table-borderless table-sm mb-0 detail-table-compact">
                        <tbody>
                          <tr>
                            <td className="p-0">
                              <span className="text-muted">
                                Uploaded by
                              </span>
                            </td>
                            <td className="p-0">
                              {openCalendarEventAttachment.team_membership ? (
                                openCalendarEventAttachment.team_membership.name
                              ) : (
                                <span>SYSTEM</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="p-0">
                              <span className="text-muted">
                                Created
                              </span>
                            </td>
                            <td className="p-0">
                              {openCalendarEventAttachment.human_created_at}
                            </td>
                          </tr>
                          <tr>
                            <td className="p-0">
                              <span className="text-muted">
                                Modified
                              </span>
                            </td>
                            <td className="p-0">
                              {openCalendarEventAttachment.human_updated_at}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="table-responsive">
                      <table className="table table-borderless table-sm mb-0 detail-table-compact">
                        <tbody>
                          <tr>
                            <td className="p-0">
                              <span className="text-muted">
                                Type
                              </span>
                            </td>
                            <td className="p-0">
                              {openCalendarEventAttachment.human_extension}
                            </td>
                          </tr>
                          {openCalendarEventAttachment.is_image ? (
                            <tr>
                              <td className="p-0">
                                <span className="text-muted">
                                  Dimensions
                                </span>
                              </td>
                              <td className="p-0">
                                {openCalendarEventAttachment.human_dimensions}
                              </td>
                            </tr>
                          ) : null}
                          <tr>
                            <td className="p-0">
                              <span className="text-muted">
                                Size
                              </span>
                            </td>
                            <td className="p-0">
                              {openCalendarEventAttachment.human_file_size}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row pt-3">
                <div className="col-12 text-center">
                  {openCalendarEventAttachment.is_image ? (
                    <img src={openCalendarEventAttachment.resolved_file_url} className="img-fluid" />
                  ) : openCalendarEventAttachment.is_pdf ? (
                    <iframe
                      src={openCalendarEventAttachment.resolved_file_url}
                      width="100%"
                      height="600px"
                      style={{border: 'none', borderRadius: '4px'}}
                      title="PDF Viewer"
                    />
                  ) : (
                    <div className="py-4">
                      <i className={"text-primary fal pb-2 preview-icon-large " + openCalendarEventAttachment.fontawesome_icon_class}></i>
                      <p className="small mb-0">Preview not available.</p>
                      <p className="small mb-0">Download file to view.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={folderedItemsToDestroy.length > 0}
        contentStyle={deletePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onClose={() => {
            stageFolderedItemsForDestroy([]);
          }
        }
      >
        <div>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col">
                  <h5 className="modal-title-mb">
                    {numberOfFilesToDestroy === 1 && numberOfFoldersToDestroy === 0 ? (
                      <strong>Delete file?</strong>
                    ) : numberOfFilesToDestroy === 0 && numberOfFoldersToDestroy === 1 ? (
                      <strong>Delete folder?</strong>
                    ) : (
                      <strong>Delete files & folders?</strong>
                    )}
                  </h5>
                </div>
                <div className="col-xs-auto">
                  <a href="#"
                     className="text-dark popup-close-btn"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         stageFolderedItemsForDestroy([]);
                       }
                      } >
                    <i className="far fa-times"></i>
                  </a>
                </div>
              </div>
              {numberOfFilesToDestroy === 1 && numberOfFoldersToDestroy === 0 ? (
                <p>You're about to delete this file and its contents.</p>
              ) : numberOfFilesToDestroy === 0 && numberOfFoldersToDestroy === 1 ? (
                <p>You're about to delete this folder and its contents.</p>
              ) : (
                <p>You're about to delete {numberOfFilesToDestroy} file(s) and {numberOfFoldersToDestroy} folder(s).</p>
              )}
              <p>This cannot be undone.</p>
              <p>Are you sure?</p>
              <div className="text-right">
                <a href="#"
                   onClick={
                     (e) => {
                       e.preventDefault();
                       stageFolderedItemsForDestroy([]);
                     }
                   }>
                  Cancel
                </a>
                <a href="#"
                   className="btn btn-danger ml-3"
                   onClick={
                     (e) => {
                       e.preventDefault();
                       deleteStagedFolderedItems(csrfToken, team, folderedItemsToDestroy);
                     }
                   }>
                  {numberOfFilesToDestroy === 1 && numberOfFoldersToDestroy === 0 ? (
                    "Yes, delete file"
                  ) : numberOfFilesToDestroy === 0 && numberOfFoldersToDestroy === 1 ? (
                    "Yes, delete folder"
                  ) : (
                    "Yes, delete files & folders"
                  )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={folderedItemsToMove.length > 0}
        contentStyle={movePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onOpen={() => {
          var updated = Object.assign({}, folder, {
            lastRequestData: undefined,
            foldered_items: []
          });

          folderCopyChanged(updated);
        }}
        onClose={() => {
            targetFolderChanged({});
            stageFolderedItemsForMove([]);
            folderCopyChanged({});
            targetFolderSearchTermChanged("");
          }
        }
      >
        <div>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col d-flex align-items-center">
                  <h5 className="mb-0">
                    <strong>
                      <i className="fas fa-arrows-alt text-primary mr-2"></i>
                      Move to...
                    </strong>
                  </h5>
                </div>
                <div className="col-xs-auto d-flex align-items-center">
                  <a href="#"
                     className="text-dark popup-close-btn"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         stageFolderedItemsForMove([], folder);
                       }
                      } >
                    <i className="far fa-times"></i>
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <SelectTargetFolder csrfToken={csrfToken}
                                      team={team}
                                      confirm={confirm}
                                      fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                                      currentUser={currentUser}
                                      folder={folder}
                                      selectedFolderedItems={folderedItemsToMove}
                                      togglefolderedItemSelected={togglefolderedItemSelected}
                                      stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                                      copiedFolderedItem={copiedFolderedItem}
                                      copiedFolderedItemChanged={copiedFolderedItemChanged}
                                      copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                                      updateFolderedItemName={updateFolderedItemName}
                                      stageFolderedItemsForMove={stageFolderedItemsForMove}
                                      fetchPermissionSet={fetchPermissionSet}
                                      folderedItemUpdated={folderedItemUpdated}
                                      targetFolder={targetFolder}
                                      targetFolderChanged={targetFolderChanged}
                                      folderCopy={folderCopy}
                                      fetchFolderedItemsForFolderCopy={fetchFolderedItemsForFolderCopy}
                                      depthWidth={depthWidth}
                                      context={MOVE_TO_FOLDER_CONTEXT}
                                      targetFolderSearchTerm={targetFolderSearchTerm}
                                      targetFolderSearchTermChanged={targetFolderSearchTermChanged}
                                      canSelectRootFolderAsTarget={false}
                                      toggleFolderCopy={toggleFolderCopy} />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <form onSubmit={
                    (e) => {
                      e.preventDefault();

                      if(canMoveStagedFolderedItems(folderedItemsToMove, targetFolder)){
                        moveStagedFolderedItems(csrfToken, team, folderedItemsToMove, targetFolder);
                      }
                    }
                  }>
                    <div className="form-row mt-3">
                      <div className="col text-right">
                        <a href="#"
                           onClick={
                             (e) => {
                               e.preventDefault();
                               stageFolderedItemsForMove([]);
                             }
                           }>
                          <strong>
                            Cancel
                          </strong>
                        </a>
                        <button type="submit"
                                disabled={!canMoveStagedFolderedItems(folderedItemsToMove, targetFolder)}
                                className="btn btn-primary ml-3">
                          <strong>Move</strong>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={isAddingFolder}
        contentStyle={newFolderPopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onOpen={() => {
          var updated = Object.assign({}, folder, {
            lastRequestData: undefined,
            foldered_items: []
          });

          folderCopyChanged(updated);
          newFolderNameChanged("New Folder");
        }}
        onClose={
          () => {
            targetFolderChanged({});
            isAddingFolderChanged(false);
            newFolderNameChanged("");
            targetFolderSearchTermChanged("");
          }
        }
      >
        <div>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col d-flex align-items-center">
                  <h5 className="mb-0">
                    <strong>
                      <i className="fas fa-folder-plus text-primary mr-2"></i>
                      New Folder
                    </strong>
                  </h5>
                </div>
                <div className="col-xs-auto d-flex align-items-center">
                  <a href="#"
                     className="text-dark popup-close-btn"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         isAddingFolderChanged(false);
                       }
                      } >
                    <i className="far fa-times"></i>
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <SelectTargetFolder csrfToken={csrfToken}
                                      team={team}
                                      confirm={confirm}
                                      fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                                      currentUser={currentUser}
                                      folder={folder}
                                      selectedFolderedItems={folderedItemsToMove}
                                      togglefolderedItemSelected={togglefolderedItemSelected}
                                      stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                                      copiedFolderedItem={copiedFolderedItem}
                                      copiedFolderedItemChanged={copiedFolderedItemChanged}
                                      copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                                      updateFolderedItemName={updateFolderedItemName}
                                      stageFolderedItemsForMove={stageFolderedItemsForMove}
                                      fetchPermissionSet={fetchPermissionSet}
                                      folderedItemUpdated={folderedItemUpdated}
                                      targetFolder={targetFolder}
                                      targetFolderChanged={targetFolderChanged}
                                      folderCopy={folderCopy}
                                      fetchFolderedItemsForFolderCopy={fetchFolderedItemsForFolderCopy}
                                      depthWidth={depthWidth}
                                      context={NEW_FOLDER_CONTEXT}
                                      targetFolderSearchTerm={targetFolderSearchTerm}
                                      targetFolderSearchTermChanged={targetFolderSearchTermChanged}
                                      canSelectRootFolderAsTarget={canSelectRootFolderAsTarget}
                                      toggleFolderCopy={toggleFolderCopy} />
                </div>
              </div>
              <form onSubmit={
                    (e) => {
                      e.preventDefault();

                      if(canCreateNewFolder(targetFolder, newFolderName)){
                        createFolder(csrfToken, team, confirm, targetFolder, newFolderName);
                      }
                    }
                  }>
                <div className="row mt-1">
                  <div className="col-12 col-md-6 d-flex align-items-center justify-content-start">
                    <div className="form-group mb-0"
                         style={{"flexGrow": "1"}}>
                      <label htmlFor="new-folder-name"
                            className='mb-0 text-muted small asset-form-label'>
                        Folder Name
                      </label>
                      <input type="text"
                            className="form-control form-control-lg asset-form-input"
                            id="new-folder-name"
                            value={newFolderName}
                            onChange={
                              (e) => {
                                newFolderNameChanged(e.target.value);
                              }
                            } />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 d-flex align-items-center justify-content-end">
                    <div className="form-row mt-3">
                      <div className="col text-right">
                        <a href="#"
                          onClick={
                            (e) => {
                              e.preventDefault();
                              isAddingFolderChanged(false);
                            }
                          }>
                          <strong>
                            Cancel
                          </strong>
                        </a>
                        <button type="submit"
                                disabled={!canCreateNewFolder(targetFolder, newFolderName)}
                                className="btn btn-primary ml-3">
                          <strong>Create</strong>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={isUploadingFiles}
        contentStyle={movePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onOpen={() => {
          var updated = Object.assign({}, folder, {
            lastRequestData: undefined,
            foldered_items: []
          });

          folderCopyChanged(updated);
          selectedFilesToUploadChanged({});
        }}
        onClose={() => {
            targetFolderChanged({});
            isUploadingFilesChanged(false);
            selectedFilesToUploadChanged({});
            targetFolderSearchTermChanged("");
          }
        }
      >
        <div>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col d-flex align-items-center">
                  <h5 className="mb-0">
                    <strong>
                      <i className="fas fa-file-plus text-primary mr-2"></i>
                      Upload Files
                    </strong>
                  </h5>
                </div>
                <div className="col-xs-auto d-flex align-items-center">
                  <a href="#"
                     className="text-dark popup-close-btn"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         isUploadingFilesChanged(false);
                       }
                      } >
                    <i className="far fa-times"></i>
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <SelectTargetFolder csrfToken={csrfToken}
                                      team={team}
                                      confirm={confirm}
                                      fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                                      currentUser={currentUser}
                                      folder={folder}
                                      selectedFolderedItems={folderedItemsToMove}
                                      togglefolderedItemSelected={togglefolderedItemSelected}
                                      stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                                      copiedFolderedItem={copiedFolderedItem}
                                      copiedFolderedItemChanged={copiedFolderedItemChanged}
                                      copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                                      updateFolderedItemName={updateFolderedItemName}
                                      stageFolderedItemsForMove={stageFolderedItemsForMove}
                                      fetchPermissionSet={fetchPermissionSet}
                                      folderedItemUpdated={folderedItemUpdated}
                                      targetFolder={targetFolder}
                                      targetFolderChanged={targetFolderChanged}
                                      folderCopy={folderCopy}
                                      fetchFolderedItemsForFolderCopy={fetchFolderedItemsForFolderCopy}
                                      depthWidth={depthWidth}
                                      context={UPLOAD_FILES_CONTEXT}
                                      targetFolderSearchTerm={targetFolderSearchTerm}
                                      targetFolderSearchTermChanged={targetFolderSearchTermChanged}
                                      canSelectRootFolderAsTarget={canSelectRootFolderAsTarget}
                                      toggleFolderCopy={toggleFolderCopy} />
                </div>
              </div>
              <form onSubmit={
                    (e) => {
                      e.preventDefault();

                      if(canUploadFiles(targetFolder, selectedFilesToUpload, isLoadingUploadedFiles)){
                        uploadFilesToFolder(csrfToken, team, confirm, selectedFilesToUpload, targetFolder);
                      }
                    }
                  }>
                <div className="row mt-3">
                  <div className='col-12 d-flex'>
                    <div>
                      <input type="file"
                            ref={fileInputRef}
                            onChange={
                              (e) => {
                                selectedFilesToUploadChanged(e.target.files);
                              }
                            }
                            style={{ display: "none" }}
                            multiple={true} />
                      <a href="#"
                        className='btn btn-primary'
                        style={{"fontSize": "14px"}}
                        onClick={
                            (e) => {
                              e.preventDefault();
                              fileInputRef.current.click();
                            }
                        }>
                        <strong>Select File(s)</strong>
                      </a>
                    </div>
                    <div className="upload-file-name-list">
                      {selectedFilesToUpload.length > 0 && (
                        Array.from(selectedFilesToUpload).map((file, index) => (
                          <div key={index}
                                className='text-muted small text-truncate upload-file-name-item'>
                            {file.name}
                          </div>
                        ))
                      )}
                    </div>
                    <div>
                      {!isLoadingUploadedFiles ? (
                        <a href="#"
                            onClick={
                              (e) => {
                                e.preventDefault();
                                isUploadingFilesChanged(false);
                              }
                            }>
                          <strong>
                            Cancel
                          </strong>
                        </a>
                      ) : null}
                      <button type="submit"
                              disabled={!canUploadFiles(targetFolder, selectedFilesToUpload, isLoadingUploadedFiles)}
                              className="btn btn-primary ml-3">
                        {isLoadingUploadedFiles ? (
                          <React.Fragment>
                            <span className="css-spinner mr-1"></span>
                            <strong>Uploading...</strong>
                          </React.Fragment>
                        ) : (
                          <strong>Upload</strong>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={Object.keys(calendarEventAttachmentToCopy).length > 0}
        contentStyle={copyFilePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onOpen={() => {
          var updated = Object.assign({}, folder, {
            lastRequestData: undefined,
            foldered_items: []
          });

          folderCopyChanged(updated);
        }}
        onClose={() => {
            targetFolderChanged({});
            targetFolderSearchTermChanged("");
            calendarEventAttachmentToCopyChanged({});
          }
        }
      >
        <div>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col d-flex align-items-center">
                  <h5 className="mb-0">
                    <strong>
                      <i className="fas fa-copy text-primary mr-2"></i>
                      Copy File
                    </strong>
                  </h5>
                </div>
                <div className="col-xs-auto d-flex align-items-center">
                  <a href="#"
                     className="text-dark popup-close-btn"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         calendarEventAttachmentToCopyChanged({});
                       }
                      } >
                    <i className="far fa-times"></i>
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <SelectTargetFolder csrfToken={csrfToken}
                                      team={team}
                                      confirm={confirm}
                                      fetchCalendarEventAttachment={fetchCalendarEventAttachment}
                                      currentUser={currentUser}
                                      folder={folder}
                                      selectedFolderedItems={folderedItemsToMove}
                                      togglefolderedItemSelected={togglefolderedItemSelected}
                                      stageFolderedItemsForDestroy={stageFolderedItemsForDestroy}
                                      copiedFolderedItem={copiedFolderedItem}
                                      copiedFolderedItemChanged={copiedFolderedItemChanged}
                                      copiedFolderedItemNameChanged={copiedFolderedItemNameChanged}
                                      updateFolderedItemName={updateFolderedItemName}
                                      stageFolderedItemsForMove={stageFolderedItemsForMove}
                                      fetchPermissionSet={fetchPermissionSet}
                                      folderedItemUpdated={folderedItemUpdated}
                                      targetFolder={targetFolder}
                                      targetFolderChanged={targetFolderChanged}
                                      folderCopy={folderCopy}
                                      fetchFolderedItemsForFolderCopy={fetchFolderedItemsForFolderCopy}
                                      depthWidth={depthWidth}
                                      context={COPY_FILE_CONTEXT}
                                      targetFolderSearchTerm={targetFolderSearchTerm}
                                      targetFolderSearchTermChanged={targetFolderSearchTermChanged}
                                      canSelectRootFolderAsTarget={false}
                                      toggleFolderCopy={toggleFolderCopy} />
                </div>
              </div>
              <form onSubmit={
                    (e) => {
                      e.preventDefault();

                      if(canCopyCalendarEventAttachment(targetFolder, calendarEventAttachmentToCopy, isCopyingCalendarEventAttachment)){
                        createCalendarEventAttachmentCopy(csrfToken, team, targetFolder, calendarEventAttachmentToCopy);
                      }
                    }
                  }>
                <div className="row mt-1">
                  <div className="col-12 col-md-6 d-flex align-items-center justify-content-start">
                    <div className="form-group mb-0"
                         style={{"flexGrow": "1"}}>
                      <label htmlFor="copy-file-name"
                            className='mb-0 text-muted small asset-form-label'>
                        File Name
                      </label>
                      <input type="text"
                            className="form-control form-control-lg asset-form-input"
                            id="copy-file-name"
                            value={calendarEventAttachmentToCopy.human_file_name || ""}
                            onChange={
                              (e) => {
                                var updated = Object.assign({}, calendarEventAttachmentToCopy, {
                                  human_file_name: e.target.value
                                });

                                calendarEventAttachmentToCopyChanged(updated);
                              }
                            } />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 d-flex align-items-center justify-content-end">
                    <div className="form-row mt-3">
                      <div className="col text-right">
                        {!isCopyingCalendarEventAttachment ? (
                          <a href="#"
                            onClick={
                              (e) => {
                                e.preventDefault();
                                calendarEventAttachmentToCopyChanged({});
                              }
                            }>
                            <strong>
                              Cancel
                            </strong>
                          </a>
                        ) : null}
                        <button type="submit"
                                disabled={!canCopyCalendarEventAttachment(targetFolder, calendarEventAttachmentToCopy, isCopyingCalendarEventAttachment)}
                                className="btn btn-primary ml-3">
                          {isCopyingCalendarEventAttachment ? (
                            <React.Fragment>
                              <span className="css-spinner mr-1"></span>
                              <strong>Copying...</strong>
                            </React.Fragment>
                          ) : (
                            <strong>Copy</strong>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Popup>
      <SetPermissionsPopup csrfToken={csrfToken}
                           team={team}
                           confirm={confirm}
                           permissionSet={permissionSet}
                           permissionSetChanged={permissionSetChanged}
                           permissionRuleEnabledClicked={permissionRuleEnabledClicked}
                           selectAllPermissionRulesClicked={selectAllPermissionRulesClicked}
                           submitPermissionSet={submitPermissionSet}
                           onPermissionSetSaved={onPermissionSetSaved}
                           guestTeamMemberships={guestTeamMemberships}
                           promotersWithPermission={promotersWithPermission}
                           currentUser={currentUser} />
      <Popup
        open={shareFolderItem !== null}
        contentStyle={deletePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onClose={() => setShareFolderItem(null)}
      >
        {shareFolderItem ? (
          <ShareFolderModal
            team={team}
            folder={shareFolderItem.item}
            csrfToken={csrfToken}
            onClose={() => setShareFolderItem(null)}
          />
        ) : null}
      </Popup>
      <Popup
        open={shareFileItem !== null}
        contentStyle={deletePopupContentStyle}
        closeOnDocumentClick={false}
        className={""}
        onClose={() => setShareFileItem(null)}
      >
        {shareFileItem ? (
          <ShareFileModal
            team={team}
            attachment={shareFileItem.item}
            csrfToken={csrfToken}
            onClose={() => setShareFileItem(null)}
          />
        ) : null}
      </Popup>
      {contextMenu ? (
        <ContextMenu
          position={contextMenu.position}
          foldered={contextMenu.foldered}
          selectedFolderedItems={selectedFolderedItems}
          team={team}
          currentUser={currentUser}
          onClose={() => setContextMenu(null)}
          onDownload={handleContextMenuDownload}
          onCopy={handleContextMenuCopy}
          onMove={handleContextMenuMove}
          onRename={handleContextMenuRename}
          onSetPermissions={handleContextMenuSetPermissions}
          onShare={handleContextMenuShare}
          onDelete={handleContextMenuDelete}
        />
      ) : null}
      <SelectionBar
        selectedCount={selectedFolderedItems.length}
        onMove={() => stageFolderedItemsForMove(selectedFolderedItems)}
        onShare={() => { if (shareableItem) handleContextMenuShare(shareableItem); }}
        shareableItem={shareableItem}
        onDelete={() => stageFolderedItemsForDestroy(selectedFolderedItems)}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};

FolderView.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  openCalendarEventAttachment: PropTypes.object,
  closeCalendarEventAttachmentModal: PropTypes.func.isRequired,
  deleteCalendarEventAttachment: PropTypes.func.isRequired,
  fetchCalendarEventAttachment: PropTypes.func.isRequired,
  openCalendarEventAttachmentPdfNumberOfPages: PropTypes.number,
  openCalendarEventAttachmentPdfNumberOfPagesChanged: PropTypes.func.isRequired,
  editOpenCalendarEventAttachmentName: PropTypes.func.isRequired,
  updateCalendarEventAttachment: PropTypes.func.isRequired,
  setOpenCalendarEventAttachmentNameEl: PropTypes.func.isRequired,
  endEditOpenCalendarEventAttachmentName: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  folder: PropTypes.object.isRequired,
  createFolder: PropTypes.func.isRequired,
  toggleFolder: PropTypes.func.isRequired,
  selectedFolderedItems: PropTypes.array,
  togglefolderedItemSelected: PropTypes.func.isRequired,
  folderedItemsToDestroy: PropTypes.array,
  stageFolderedItemsForDestroy: PropTypes.func.isRequired,
  deleteStagedFolderedItems: PropTypes.func.isRequired,
  copiedFolderedItem: PropTypes.object,
  copiedFolderedItemChanged: PropTypes.func.isRequired,
  copiedFolderedItemNameChanged: PropTypes.func.isRequired,
  updateFolderedItemName: PropTypes.func.isRequired,
  folderedItemsToMove: PropTypes.array,
  stageFolderedItemsForMove: PropTypes.func.isRequired,
  moveStagedFolderedItems: PropTypes.func.isRequired,
  fetchPermissionSet: PropTypes.func.isRequired,
  folderedItemUpdated: PropTypes.func.isRequired,
  fetchFolderedItemsForFolder: PropTypes.func.isRequired,
  targetFolder: PropTypes.object,
  targetFolderChanged: PropTypes.func.isRequired,
  folderCopy: PropTypes.object,
  folderCopyChanged: PropTypes.func.isRequired,
  fetchFolderedItemsForFolderCopy: PropTypes.func.isRequired,
  toggleFolderCopy: PropTypes.func.isRequired,
  onFolderChanged: PropTypes.func.isRequired,
  permissionSet: PropTypes.object,
  permissionSetChanged: PropTypes.func.isRequired,
  permissionRuleEnabledClicked: PropTypes.func.isRequired,
  selectAllPermissionRulesClicked: PropTypes.func.isRequired,
  submitPermissionSet: PropTypes.func.isRequired,
  onPermissionSetSaved: PropTypes.func.isRequired,
  guestTeamMemberships: PropTypes.array,
  promotersWithPermission: PropTypes.array,
  headerComponent: PropTypes.func.isRequired,
  menuComponent: PropTypes.func,
  isAddingFolder: PropTypes.bool,
  isAddingFolderChanged: PropTypes.func.isRequired,
  newFolderName: PropTypes.string,
  newFolderNameChanged: PropTypes.func.isRequired,
  isUploadingFiles: PropTypes.bool,
  isUploadingFilesChanged: PropTypes.func.isRequired,
  selectedFilesToUpload: PropTypes.object,
  selectedFilesToUploadChanged: PropTypes.func.isRequired,
  uploadFilesToFolder: PropTypes.func.isRequired,
  isLoadingUploadedFiles: PropTypes.bool,
  canSelectRootFolderAsTarget: PropTypes.bool,
  fetchGuestsAndPromotersForPermissions: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  targetFolderSearchTerm: PropTypes.string,
  targetFolderSearchTermChanged: PropTypes.func.isRequired,
  calendarEventAttachmentToCopy: PropTypes.object,
  calendarEventAttachmentToCopyChanged: PropTypes.func.isRequired,
  createCalendarEventAttachmentCopy: PropTypes.func.isRequired,
  isCopyingCalendarEventAttachment: PropTypes.bool,
  teamRootFolder: PropTypes.object.isRequired,
  draggedFilesToUpload: PropTypes.array,
  draggedFilesToUploadChanged: PropTypes.func.isRequired,
  showConfirmIdColumn: PropTypes.bool,
  folderChanged: PropTypes.func.isRequired,
  uploadFolderStructure: PropTypes.func,
  navigateToFolder: PropTypes.func,
  onDropWithoutSelection: PropTypes.func,
  noSelectionDropFolder: PropTypes.object
};

export default FolderView;
